import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message, Input, Form, Alert } from "antd";
import { createUnit, createUnitAlias, getAllUnits, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const UnitComponent = ({ exception, onClose, onResolved }) => {
    const [unitName, setUnitName] = useState('');
    const [unitDescription, setUnitDescription] = useState('');
    const [unitCode, setUnitCode] = useState('');
    const [showSelect, setShowSelect] = useState(false);
    const [units, setUnits] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const extractUnitDetails = () => {
            const unknownInfo = exception?.unknown_infomation || '';
            setUnitName(unknownInfo);
            setUnitDescription(unknownInfo);
            setUnitCode(unknownInfo);
            setTimeout(() => {
                form.setFieldsValue({
                    unitName: unknownInfo,
                    unitDescription: unknownInfo,
                    unitCode: unknownInfo
                });
            }, 100);
        };

        if (exception) {
            extractUnitDetails();
        }

        const fetchUnits = async () => {
            setLoading(true);
            try {
                const response = await getAllUnits();
                const unitsData = response?.data?.data || [];
                setUnits(Array.isArray(unitsData) ? unitsData : []);
            } catch (error) {
                message.error('Failed to fetch units');
            } finally {
                setLoading(false);
            }
        };

        fetchUnits();
    }, [exception]);

    const handleMarkAsNew = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            setErrors({}); 
            
            if (exception.status === 'pending') {
                const payload = {
                    id: 0,
                    name: values.unitName,
                    description: values.unitDescription,
                    code: values.unitCode,
                    alias_for: 0,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                };
                
                await createUnit(payload);
                await updateException(exception.id);
                message.success('Unit created');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                const backendError = error.response?.data;
                if (backendError?.errors) {
                    setErrors(backendError.errors);
                    message.error(backendError.message || 'Validation failed');
                } else {
                    const errorMessage = backendError?.message || 'Failed to create unit';
                    message.error(errorMessage);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleUnitSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        
        try {
            const values = await form.validateFields();
            setLoading(true);
            setErrors({}); 
            
            if (exception.status === 'pending') {
                const payload = {
                    name: values.unitName,
                    description: values.unitDescription,
                    code: ` ${values.unitCode}-alias`,
                    alias_for: selectedAliasId
                };
                
                await createUnitAlias(payload);
                await updateException(exception.id);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } else {
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else {
                const backendError = error.response?.data;
                if (backendError?.errors) {
                    setErrors(backendError.errors);
                    message.error(backendError.message || 'Validation failed');
                } else {
                    const errorMessage = backendError?.message || 'Failed to save alias';
                    message.error(errorMessage);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    {Object.keys(errors).length > 0 && (
                        <Alert
                            message="Validation Errors"
                            description={
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {Object.entries(errors).map(([field, fieldErrors]) => (
                                        <li key={field}>
                                            <strong>{field}:</strong> {Array.isArray(fieldErrors) ? fieldErrors.join(', ') : fieldErrors}
                                        </li>
                                    ))}
                                </ul>
                            }
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}
                    
                    <Form form={form} layout="vertical" style={{ marginBottom: 20 }}>
                        <Form.Item
                            label="Unit Name"
                            name="unitName"
                            rules={[{ required: true, message: 'Please enter unit name' }]}
                        >
                            <Input placeholder="Enter unit name (e.g., Meter)" />
                        </Form.Item>
                        
                        <Form.Item
                            label="Unit Description"
                            name="unitDescription"
                            rules={[{ required: true, message: 'Please enter unit description' }]}
                        >
                            <Input placeholder="Enter unit description (e.g., Unit of length)" />
                        </Form.Item>

                        <Form.Item
                            label="Unit Code"
                            name="unitCode"
                            rules={[{ required: true, message: 'Please enter unit code' }]}
                        >
                            <Input placeholder="Enter unit code (e.g., m)" />
                        </Form.Item>
                    </Form>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666', marginTop: 16, marginBottom: 8 }}>
                                    Select an existing unit to alias the new unit to:
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a unit"
                                    onChange={handleUnitSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {units.map((unit) => (
                                        <Option key={unit.id} value={unit.id}>
                                            {unit.name} ({unit.code}) - {unit.description}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save Alias
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default UnitComponent;
